import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useLocation } from 'wouter'
import WaiverForm, { WaiverFormResult } from '../components/WaiverForm'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
	AlertCircle,
	Check,
	Loader2,
	ChevronLeft,
	Wallet
} from 'lucide-react'
import Header from '../components/Header'
import { useLoading } from '../components/LoadingProvider'
import { Card } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { chargePayment, isCardUpdateRequiredError } from '@/utils/charge'
import { useUser } from '@/hooks/use-user'
import { getSignedTransactionData } from '@/utils/transactions'
import { mutate } from 'swr'
import { ICachedMembershipSelection } from '@/constants'
import { IS_TESTNET } from '@/constants/walletConnectionConfig'
import { usePrices } from '@/hooks/use-prices'
import PaymentMethodSelector from '@/components/PaymentMethodSelector'
import { usePaymentConfig, getPaymentMethodBlockedReason } from '@/hooks/use-payment-config'
import type { SelectablePaymentMethod } from '@/types/payment-methods'
import { DEFAULT_PAYMENT_METHOD } from '@/types/payment-methods'
import PromoCodeInput from '@/components/PromoCodeInput'
import type { ApplicableDiscount } from '@/types/pos'
import { apiFetch } from '@/utils'
import { useDeviceIntegrity } from '@/providers/DeviceIntegrityProvider'

type WaiverStep =
	| 'secrets_hideaway'
	| 'club_swinkster'
	| 'payment'
	| 'completed'

export default function WaiverPage() {
	const [currentStep, setCurrentStep] = useState<WaiverStep>('secrets_hideaway')
	const [isRedirecting, setIsRedirecting] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [completedWaivers, setCompletedWaivers] = useState<string[]>([])
	const [isTransitioning, setIsTransitioning] = useState(false)
	const [isProcessingPayment, setIsProcessingPayment] = useState(false)
	const [isConnectingWallet, setIsConnectingWallet] = useState(false)
	const [membershipData, setMembershipData] = useState<ICachedMembershipSelection | null>(null)
	const [, navigate] = useLocation()
	const { showLoader } = useLoading()
	const { uid, isConnected, walletAddress, connectWallet } = useUser()
	const { toast } = useToast()
	const { convertUsdToToken } = usePrices()
	const { requireAcknowledgement } = useDeviceIntegrity()
	const { config: paymentConfig } = usePaymentConfig()
	const timeouts = useRef<Set<NodeJS.Timeout>>(new Set())
	const isMounted = useRef(true)
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<SelectablePaymentMethod>(DEFAULT_PAYMENT_METHOD)
	const [appliedDiscount, setAppliedDiscount] = useState<ApplicableDiscount | null>(null)

	const [completedWaiverPayloads, setCompletedWaiverPayloads] = useState<
		WaiverFormResult[]
	>([])
	const [manualPromoApplied, setManualPromoApplied] = useState(false)
	const [autoApplyDismissed, setAutoApplyDismissed] = useState(false)
	const prevPaymentType = useRef(selectedPaymentMethod.type)

	const isCardPayment = selectedPaymentMethod.type === 'CARD'

	// Fetch auto-apply discount for membership
	const fetchAutoApplyDiscount = useCallback(async () => {
		if (!membershipData?.taxCalculation) return
		try {
			const res = await apiFetch('/v1/pos/discounts/validate', {
				method: 'POST',
				withCredentials: true,
				body: JSON.stringify({
					items: [],
					locationId: 0,
					cartSubtotal: membershipData.taxCalculation.subtotal,
					customerId: uid,
					transactionType: 'membership',
					membershipTemplateId: membershipData.template.id,
					guestType: membershipData.guestType,
					paymentMethod: selectedPaymentMethod.type,
				}),
			})
			if (!res.ok) return
			const data = await res.json()
			if (data.bestDiscount) {
				setAppliedDiscount(data.bestDiscount)
			} else {
				setAppliedDiscount(null)
			}
		} catch {
			// Silently fail - auto-apply is best-effort
		}
	}, [membershipData, uid, selectedPaymentMethod.type])

	// Auto-apply discounts when reaching the payment step
	useEffect(() => {
		if (currentStep !== 'payment' || !membershipData?.taxCalculation || manualPromoApplied || autoApplyDismissed) return
		fetchAutoApplyDiscount()
	}, [currentStep, membershipData, manualPromoApplied, autoApplyDismissed, fetchAutoApplyDiscount])

	// Re-validate discount when payment method changes
	useEffect(() => {
		const changed = prevPaymentType.current !== selectedPaymentMethod.type
		prevPaymentType.current = selectedPaymentMethod.type
		if (!changed || currentStep !== 'payment' || !membershipData) return

		// Payment method changed — clear discount state; auto-apply effect will re-validate
		setAppliedDiscount(null)
		setManualPromoApplied(false)
		setAutoApplyDismissed(false)
	}, [selectedPaymentMethod.type, currentStep, membershipData])

	// Handle removing a discount — fallback to auto-apply like the POS does
	const handleRemoveDiscount = useCallback(() => {
		if (appliedDiscount && appliedDiscount.isAutoApply && !manualPromoApplied) {
			// User dismissed an auto-apply discount
			setAutoApplyDismissed(true)
			setAppliedDiscount(null)
		} else {
			// User removed a manual promo — fall back to auto-apply
			setManualPromoApplied(false)
			setAppliedDiscount(null)
			// Re-trigger auto-apply
			fetchAutoApplyDiscount()
		}
	}, [appliedDiscount, manualPromoApplied, fetchAutoApplyDiscount])

	const clearTimeouts = useCallback(() => {
		timeouts.current.forEach((timeout) => {
			clearTimeout(timeout)
		})
		timeouts.current.clear()
	}, [])

	const addTimeout = useCallback((timeout: NodeJS.Timeout) => {
		if(isMounted.current) {
			timeouts.current.add(timeout)
		}
		return timeout
	}, [])

	// Check for membership data
	useEffect(() => {
		const checkMembership = () => {
			const data = localStorage.getItem('selectedMembership')

			if(!data) {
				setError('Please select a membership plan before proceeding.')
				addTimeout(setTimeout(() => navigate('/membership'), 3000))
				return
			}

			try {
				const membership = JSON.parse(data) as ICachedMembershipSelection
				const timeSincePurchase = Date.now() - membership.timestamp

				if(timeSincePurchase > 3600000) {
					localStorage.removeItem('selectedMembership')
					setError(
						'Your membership selection has expired. Redirecting to memberships...'
					)
					addTimeout(setTimeout(() => navigate('/membership'), 3000))
					return
				}

				// Validate new template-based format
				if(!membership.template || !membership.guestType) {
					localStorage.removeItem('selectedMembership')
					setError('Membership data is outdated. Redirecting to memberships...')
					addTimeout(setTimeout(() => navigate('/membership'), 3000))
					return
				}

				// Validate that tax calculation exists
				if(!membership.taxCalculation) {
					setError('Tax calculation missing. Redirecting to memberships...')
					addTimeout(setTimeout(() => navigate('/membership'), 3000))
					return
				}

				// Calculate SECH price from the TOTAL USD amount (including taxes), not subtotal
				const totalUsdPrice = membership.taxCalculation.total
				const correctedSechPrice = convertUsdToToken(totalUsdPrice, 'sech')

				// Update the membership data with the correct SECH price
				const correctedMembership = {
					...membership,
					sechPrice: correctedSechPrice,
					totalSechPrice: correctedSechPrice,
					appliedDiscount: null
				}

				setMembershipData(correctedMembership)
				setError(null)
			} catch(error) {
				console.error('Error parsing membership data:', error)
				localStorage.removeItem('selectedMembership')
				setError('Invalid membership data. Please select a membership plan.')
			}
		}

		checkMembership()
	}, [])

	useEffect(() => {
		isMounted.current = true
		return () => {
			isMounted.current = false
			clearTimeouts()
			setIsTransitioning(false)
			setIsRedirecting(false)
			setError(null)
			setCompletedWaivers([])
		}
	}, [clearTimeouts])

	const handleWaiverComplete = useCallback(
		(result: WaiverFormResult) => {
			clearTimeouts()

			setCompletedWaivers((prev) => [...prev, currentStep])

			if(result.type === 'secrets_hideaway') {
				setCompletedWaiverPayloads((_prev) => [result])
			} else {
				setCompletedWaiverPayloads((prev) => [prev[0], result])
			}

			if(currentStep === 'secrets_hideaway') {
				setIsTransitioning(true)
				const timeout = setTimeout(() => {
					if(isMounted.current) {
						setCurrentStep('club_swinkster')
						setIsTransitioning(false)
					}
				}, 300)
				addTimeout(timeout)
			} else if(currentStep === 'club_swinkster') {
				setIsTransitioning(true)
				const timeout = setTimeout(() => {
					if(isMounted.current) {
						setCurrentStep('payment')
						setIsTransitioning(false)
					}
				}, 300)
				addTimeout(timeout)
			}
		},
		[currentStep, addTimeout, clearTimeouts]
	)

	const handlePayment = useCallback(async () => {
		if(isProcessingPayment || isConnectingWallet) return

		try {
			setError(null)

			// Redundancy check: confirm the selected payment method is still accepted
			const blocked = getPaymentMethodBlockedReason(selectedPaymentMethod.type, paymentConfig)
			if (blocked) {
				throw new Error(blocked)
			}

			// Verify membership data
			if(!membershipData) {
				throw new Error(
					'Membership data not found. Please select a membership plan.'
				)
			}

			// Verify tax calculation exists
			if(!membershipData.taxCalculation) {
				throw new Error('Tax calculation missing. Please reselect your membership plan.')
			}

			const proceed = await requireAcknowledgement('this payment')
			if (!proceed) {
				return
			}

			// Start payment processing
			setIsProcessingPayment(true)

			// Calculate final amounts with discount
			const discountAmt = appliedDiscount ? appliedDiscount.calculatedAmount : 0
			const finalUsdTotal = membershipData.taxCalculation.total - discountAmt
			const finalSechPrice = convertUsdToToken(finalUsdTotal, 'sech')

			let signature: string | undefined
			let forwardRequest: unknown | undefined

			if (selectedPaymentMethod.type === 'TOKEN') {
				// TOKEN flow: connect wallet + get signature
				if(!walletAddress) {
					setIsConnectingWallet(true)
					try {
						const address = await connectWallet()
						if(!address) {
							throw new Error('Please connect your wallet to continue')
						}
					} catch(error) {
						throw new Error(
							error instanceof Error ? error.message : 'Failed to connect wallet'
						)
					} finally {
						setIsConnectingWallet(false)
					}
				}

				toast({
					title: 'Processing Payment',
					description: `Processing ${finalSechPrice} SECH payment...`
				})

				if(isConnected) {
					const {
						signedTx,
						transferRequest: signatureMessage
					} = await getSignedTransactionData({
						userAddress: walletAddress,
						amount: finalSechPrice.toString()
					})
					signature = signedTx
					forwardRequest = signatureMessage
				}
			} else {
				// CARD flow: no wallet signature needed
				toast({
					title: 'Processing Payment',
					description: 'Processing credit card payment...'
				})
			}

			// Process payment through unified charge endpoint
			await chargePayment({
				transactionType: 'membership',
				paymentMethodType: selectedPaymentMethod.type,
				amount: membershipData.taxCalculation.total,
				...(selectedPaymentMethod.type === 'CARD' && { paymentMethodId: selectedPaymentMethod.id }),
				userUid: uid,
				walletAddress,
				...(selectedPaymentMethod.type === 'TOKEN' && { signature, forwardRequest }),
				...(appliedDiscount && {
					discountId: appliedDiscount.discountId,
					discountCode: appliedDiscount.promoCode,
					discountAmount: appliedDiscount.calculatedAmount,
				}),
				context: {
					templateId: membershipData.template.id,
					membershipTemplateId: membershipData.template.id,
					guestType: membershipData.guestType,
					waivers: completedWaiverPayloads.map((wv) => ({
						...wv,
						customerAddress: walletAddress,
						hasPartner: wv.attendanceStatus === 'couple' && wv.partnerFullName !== ''
					})),
				},
			})

			// Success handling
			toast({
				title: 'Payment Successful',
				description: `Your ${membershipData.template.name} membership has been activated.`
			})

			mutate(`/v1/transactions/${uid}`)
			setIsRedirecting(true)
			showLoader(500)
			const timeout = setTimeout(() => {
				if(isMounted.current) {
					localStorage.removeItem('selectedMembership')
					navigate('/entry-passes')
				}
			}, 500)
			addTimeout(timeout)
		} catch(error) {
			// Invalid card vault: route the user to re-enter their card instead of
			// retrying the same (now-flagged) card.
			if (isCardUpdateRequiredError(error)) {
				toast({
					title: 'Card Update Required',
					description: 'Please re-enter your card details to continue.',
					variant: 'destructive'
				})
				navigate('/account/payment-methods')
				return
			}
			const errorMessage =
				error instanceof Error ? error.message : 'Failed to process payment'
			setError(errorMessage)
			toast({
				title: 'Payment Failed',
				description: errorMessage,
				variant: 'destructive'
			})
		} finally {
			setIsProcessingPayment(false)
		}
	}, [isProcessingPayment, isConnectingWallet, membershipData, connectWallet, walletAddress, completedWaiverPayloads, navigate, addTimeout, showLoader, toast, isConnected, uid, selectedPaymentMethod, appliedDiscount, convertUsdToToken, requireAcknowledgement, paymentConfig])

	const handleError = useCallback(
		(error: string) => {
			setError(error)
			clearTimeouts()

			const errorTimeout = setTimeout(() => {
				if(isMounted.current) {
					setError(null)
				}
			}, 4000)

			addTimeout(errorTimeout)
		},
		[addTimeout, clearTimeouts]
	)

	const handleBackToFirst = useCallback(() => {
		if(isTransitioning) return

		clearTimeouts()
		setIsTransitioning(true)

		const timeout = setTimeout(() => {
			if(isMounted.current) {
				setCurrentStep('secrets_hideaway')
				setIsTransitioning(false)
			}
		}, 300)

		addTimeout(timeout)
	}, [isTransitioning, clearTimeouts, addTimeout])

	const handleBackToWaivers = () => {
		if(isTransitioning) return

		setCurrentStep('club_swinkster')
	}

	const savedFormResult = useMemo(() => {
		const index = currentStep === 'secrets_hideaway' ? 0 : 1

		//This value is used by WaiverForm to prefill the form
		//So if we have specific set filled, we provide it directly
		//Otherwise we provide the first one, because usually the data is equal there to first step
		//Null otherwise
		return completedWaiverPayloads[index] || completedWaiverPayloads[0] || null
	}, [currentStep, completedWaiverPayloads])

	const formatPrice = (amount: number) => {
		return Number(amount).toFixed(IS_TESTNET ? 6 : 2)
	}

	const formatAmount = (usdAmount: number) => {
		if (isCardPayment) {
			return `$${formatPrice(usdAmount)}`
		}
		return `${formatPrice(convertUsdToToken(usdAmount, 'sech'))} SECH`
	}

	return (
		<div className="min-h-auto bg-[#18181B]">
			<div className="max-w-5xl mx-auto px-5 pb-8">
				<div
				role="banner"
				className="sticky top-0 left-[20px]	z-10 bg-[#181b1b] pb-[12px] backdrop-blur-[8px] mb-[12px] h-auto flex items-center	justify-start gap-2	text-white py-3"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/membership')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className='h-[22px] w-[22px] text-[#ffffff]'/>
					</button>

					<h1 className="text-base font-semibold tracking-wide">
						Waivers
					</h1>
				</div>

				{error && (
					<Alert
						variant="destructive"
						className="mb-4 animate-in slide-in-from-top-2">
						<AlertCircle className="h-4 w-4"/>
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}

				<div className="mb-6">
					<h1 className="text-[16px] leading-[18px] font-semibold font-figtree text-[#ffffff] mb-2">Required Steps</h1>
					<p className="text-[13px] leading-[15px] font-normal text-[#BABABB]">
						Please complete both waivers and process payment to continue.
					</p>
				</div>

				<div className="mb-8">
					<div className="flex justify-between mb-2">
						<div className="flex items-center">
							<div
								className={`w-8 h-8 min-w-8 rounded-full flex items-center justify-center transition-all duration-300 ${
									completedWaivers.includes('secrets_hideaway')
										? 'bg-primary text-primary-foreground scale-110'
										: currentStep === 'secrets_hideaway'
											? 'bg-primary/20 text-primary'
											: 'bg-[#3C3C3C] text-[#BABABB]'
								}`}>
								{completedWaivers.includes('secrets_hideaway') ? (
									<Check className="h-[16px] w-[16px] min-w-[16px]"/>
								) : (
									'1'
								)}
							</div>
							<span className="ml-2 text-sm font-medium text-[#ffffff]">
								Secrets Hideaway Waiver
							</span>
						</div>

						<div className="flex items-center">
							<div
								className={`w-8 h-8 min-w-8 rounded-full flex items-center justify-center transition-all duration-300 ${
									completedWaivers.includes('club_swinkster')
										? 'bg-primary text-primary-foreground scale-110'
										: currentStep === 'club_swinkster'
											? 'bg-primary/20 text-primary'
											: 'bg-[#3C3C3C] text-[#BABABB]'
								}`}>
								{completedWaivers.includes('club_swinkster') ? (
									<Check className="h-4 w-4"/>
								) : (
									'2'
								)}
							</div>
							<span className="ml-2 text-sm font-medium text-[#ffffff]">
								Club Swinkster Waiver
							</span>
						</div>

						<div className="flex items-center">
							<div
								className={`w-8 h-8 min-w-8 rounded-full flex items-center justify-center transition-all duration-300 ${
									currentStep === 'payment'
										? 'bg-primary/20 text-primary'
										: currentStep === 'completed'
											? 'bg-primary text-primary-foreground scale-110'
											: 'bg-[#3C3C3C] text-[#BABABB]'
								}`}>
								{currentStep === 'completed' ? (
									<Check className="h-4 w-4"/>
								) : (
									'3'
								)}
							</div>
							<span className="ml-2 text-sm font-medium text-[#ffffff]">Payment</span>
						</div>
					</div>

					<div className="h-2 bg-[#3C3C3C] rounded-full overflow-hidden">
						<div
							className="h-full bg-primary rounded-full transition-all duration-500 ease-in-out"
							style={{
								width:
									currentStep === 'secrets_hideaway'
										? '0%'
										: currentStep === 'club_swinkster'
											? '33%'
											: currentStep === 'payment'
												? '66%'
												: '100%'
							}}
						/>
					</div>
				</div>

				<div
					className={`transition-all duration-300 ${
						isTransitioning ? 'opacity-50 scale-98' : 'opacity-100 scale-100'
					}`}>
					{isRedirecting ? (
						<Alert className="bg-primary/10 border-primary/20 animate-in fade-in-0">
							<Check className="h-4 w-4 text-primary"/>
							<AlertDescription className="flex items-center gap-2">
								<Loader2 className="h-4 w-4 animate-spin"/>
								<span>Redirecting to entry pass selection...</span>
							</AlertDescription>
						</Alert>
					) : (
						<>
							<div className="flex justify-between items-center mb-6">
								<div className="space-y-1">
									<div className="text-sm font-medium text-[#ffffff]">
										Step{' '}
										{currentStep === 'secrets_hideaway'
											? '1'
											: currentStep === 'club_swinkster'
												? '2'
												: '3'}{' '}
										of 3
									</div>
									<div className="text-sm text-[#BABABB]">
										{currentStep === 'secrets_hideaway'
											? 'Secrets Hideaway Waiver'
											: currentStep === 'club_swinkster'
												? 'Club Swinkster Waiver'
												: 'Process Payment'}
									</div>
								</div>
								{currentStep === 'club_swinkster' && !isTransitioning && (
									<Button
										variant="outline"
										onClick={handleBackToFirst}
										disabled={isTransitioning}
										className="transition-all duration-200 bg-[#3C3C3C] border-[#4C4C4C] text-[#ffffff] hover:bg-[#4C4C4C]">
										Back to First Waiver
									</Button>
								)}
							</div>

							{currentStep === 'payment' ? (
								<Card className="p-6 bg-[#3C3C3C] border-0">
									<h2 className="text-2xl font-bold mb-4 text-[#ffffff]">
										Complete Your Membership
									</h2>
									<div className="space-y-4">
										{/* Payment Method Selection — override POS vars for dark theme */}
										<div style={{
											'--pos-primary-background-color': '#2F2F32',
											'--pos-primary-background-hover-color': '#3C3C3C',
											'--pos-primary-text-color': '#ffffff',
											'--pos-secondary-text-color': '#BABABB',
											'--pos-input-border-color': '#4C4C4C',
											'--pos-primary-color': '#EF4444',
										} as React.CSSProperties}>
											<PaymentMethodSelector
												selectedMethod={selectedPaymentMethod}
												onSelect={setSelectedPaymentMethod}
											/>
										</div>

										{membershipData && (
											<div className="p-4 bg-[#2F2F32] rounded-lg border border-[#4C4C4C]">
												<div className="text-sm text-[#BABABB] mb-2">
													Selected Membership
												</div>
												<div className="font-medium mb-3 text-[#ffffff]">
													{membershipData.template.name} — <span className="text-[#BABABB] capitalize">{membershipData.guestType.replace(/_/g, ' ')}</span>
												</div>

												{/* Price breakdown */}
												{membershipData.taxCalculation && (
													<Card className="p-4 bg-[#18181B] border border-[#4C4C4C]">
														<div className="space-y-2 text-sm">
															<div className="flex justify-between">
																<span className="text-[#BABABB]">Subtotal</span>
																<span className="text-[#ffffff]">{formatAmount(membershipData.taxCalculation.subtotal)}</span>
															</div>
															{appliedDiscount && (
																<div className="flex justify-between text-green-400">
																	<span>Discount</span>
																	<span>-{formatAmount(appliedDiscount.calculatedAmount)}</span>
																</div>
															)}
															<hr className="my-2 border-[#4C4C4C]"/>
															{membershipData.taxCalculation.fees.map((fee, index) => (
																<div key={index} className="flex justify-between text-[#BABABB]">
																	<span>{fee.description} ({(fee.rate * 100).toFixed(1)}%)</span>
																	<span>{formatAmount(fee.amount)}</span>
																</div>
															))}
															<div className="flex justify-between font-semibold">
																<span className="text-[#ffffff]">Total</span>
																<span className="text-[#ffffff]">{formatAmount(appliedDiscount ? membershipData.taxCalculation.total - appliedDiscount.calculatedAmount : membershipData.taxCalculation.total)}</span>
															</div>
														</div>

														{/* Promo Code */}
														<PromoCodeInput
															transactionType="membership"
															subtotal={membershipData.taxCalculation.subtotal}
															membershipTemplateId={membershipData.template.id}
															guestType={membershipData.guestType}
															paymentMethod={selectedPaymentMethod.type}
															onDiscountChange={(discount) => {
																setAppliedDiscount(discount)
																setManualPromoApplied(!!discount)
															}}
															appliedDiscount={appliedDiscount}
															onRemoveDiscount={handleRemoveDiscount}
														/>
													</Card>
												)}
											</div>
										)}

										<Button
											className="w-full relative"
											onClick={handlePayment}
											disabled={isProcessingPayment || isConnectingWallet}>
											{isProcessingPayment ? (
												<>
													<Loader2 className="mr-2 h-4 w-4 animate-spin"/>
													Processing Payment...
												</>
											) : isConnectingWallet ? (
												<>
													<Loader2 className="mr-2 h-4 w-4 animate-spin"/>
													Connecting Wallet...
												</>
											) : selectedPaymentMethod.type === 'CARD' ? (
												'Pay with Card'
											) : !walletAddress ? (
												<>
													<Wallet className="mr-2 h-4 w-4"/>
													Connect Wallet & Pay
												</>
											) : (
												'Complete Payment'
											)}
										</Button>

										<Button
											variant="outline"
											className="w-full bg-[#2F2F32] border-[#4C4C4C] text-[#ffffff] hover:bg-[#4C4C4C]"
											onClick={handleBackToWaivers}
											disabled={isProcessingPayment || isConnectingWallet}>
											Back to Waivers
										</Button>

										<p className="text-sm text-[#BABABB] text-center">
											{selectedPaymentMethod.type === 'CARD'
												? 'Payment will be charged to your saved card'
												: 'Payment will be processed using your selected payment method'}
										</p>
									</div>
								</Card>
							) : (
								<WaiverForm
									key={currentStep}
									savedFormResult={savedFormResult}
									type={currentStep as 'secrets_hideaway' | 'club_swinkster'}
									onComplete={handleWaiverComplete}
									onError={handleError}
									isTransitioning={isTransitioning}
									guestType={membershipData?.guestType}
								/>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	)
}